import { useState } from 'react';
import type { ShortTextConfig, ShortTextValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface ShortTextProps {
  field: Pick<Field, 'label' | 'allowFootnote'>;
  config: ShortTextConfig;
  value: ShortTextValue | null;
  onCommit: (value: ShortTextValue | null) => void;
  disabled?: boolean;
}

export function ShortText({ field, config, value, onCommit, disabled = false }: ShortTextProps) {
  const [text, setText] = useState(value?.text ?? '');
  const [note, setNote] = useState(value?.note ?? '');

  function commit() {
    if (disabled) {
      return;
    }
    if (text.trim() === '') {
      onCommit(null);
      return;
    }
    onCommit({
      type: 'SHORT_TEXT',
      text: text.slice(0, config.maxLength),
      footnote: value?.footnote ?? false,
      note: value?.note ?? null,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm text-text-muted">
        <span className="eyebrow">
          {field.label} · {text.length}/{config.maxLength}
        </span>
        <input
          type="text"
          value={text}
          disabled={disabled}
          maxLength={config.maxLength}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
          className="rounded-md border border-border-strong bg-bg px-3 py-2 text-text focus:border-accent"
        />
      </label>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
